import { EventStatus } from "@/types/enums/event";

import {
  type AppointmentClockOf,
  type SidebarVisit,
  byStartAsc,
  isOnToday,
} from "./appointment-checklist-visibility";

/**
 * The Appointments section's groups.
 *
 * `todayAppointments` are appointments today that have started and are not
 * completed, a no-show or a cancelled one included: without their own group
 * they sat in neither of the others, and the next visit's checklist took
 * their place (Pylon #9220). `upcoming` have not started; `past` are
 * completed. Each is earliest or nearest first.
 */
export function getPatientSidebarAppointmentGroups<T extends SidebarVisit>(
  events: readonly T[] | null | undefined,
  clockOf: AppointmentClockOf,
  now = new Date(),
) {
  const nowMs = now.getTime();
  const appointmentsWithStart = (events ?? []).filter(
    (event) => event.start && Number.isFinite(new Date(event.start).getTime()),
  );
  const startMs = (event: T) => new Date(event.start).getTime();

  const todayAppointments = appointmentsWithStart
    .filter(
      (event) =>
        startMs(event) <= nowMs &&
        event.status !== EventStatus.COMPLETED &&
        isOnToday(event, clockOf),
    )
    .sort(byStartAsc);

  const upcomingAppointments = appointmentsWithStart
    .filter((event) => startMs(event) > nowMs)
    .sort(byStartAsc);

  const pastAppointments = appointmentsWithStart
    .filter(
      (event) =>
        startMs(event) <= nowMs && event.status === EventStatus.COMPLETED,
    )
    .sort((a, b) => byStartAsc(b, a));

  return { todayAppointments, upcomingAppointments, pastAppointments };
}
